import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context';
import { getMerchandise, type MerchandiseItem } from '../../api/merchandise';
import { accountCardsWithPassword } from '../../api/paymentPassword';
import { start as startCart, setContents } from '../../api/cart';
import { cardOnFile } from '../../api/payment';
import { ActivityIndicator, Button, PasswordDialog } from '../../components/ui';
import { RechargeItemPicker, ListItemFee } from '../../components';
import { isGuestSalesActive } from '../../utils/event';
import { format } from 'date-fns';
import { images } from '../../config/colors';
import tocBackgroundFallback from '../../assets/tocBackground.jpg';
import styles from './CoinPurchasePage.module.css';

const tocBackground = images.background || tocBackgroundFallback;
const sponsorImage = images.coinPurchaseSponsor;

interface Card {
  label: string;
  value: string;
  last_four: string;
}

interface ItemSelection {
  id: number;
  unit_price_cents: number;
  quantity: number;
}

interface LocationState {
  account?: { id: number; cid: string };
  event?: {
    id: number;
    app_enable?: string;
    start: string;
    end: string;
    fee?: { name?: string; amount?: number };
  };
}

export function CoinPurchasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginKey } = useAuth();
  const { account, event } = (location.state as LocationState) || {};

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<MerchandiseItem[]>([]);
  const [categoryId, setCategoryId] = useState(0);
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = useState('');
  const [selections, setSelections] = useState<Record<number, ItemSelection>>({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [emptyListText, setEmptyListText] = useState('Loading...');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [saveCard, setSaveCard] = useState(true);

  const feeAmount = Math.max(0, Number(event?.fee?.amount || 0));

  const calculateTotal = useCallback(() => {
    let total = feeAmount;
    Object.values(selections).forEach((item) => {
      total += item.quantity * item.unit_price_cents;
    });
    return total;
  }, [selections, feeAmount]);

  const itemCount = Object.values(selections).reduce((sum, item) => sum + item.quantity, 0);
  const total = calculateTotal();

  useEffect(() => {
    if (!event) return;

    const startDate = new Date(event.app_enable || event.start);
    const endDate = new Date(event.end);
    const now = new Date();

    if (now < startDate) {
      const hoursDiff = (startDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      let displayText: string;

      if (hoursDiff < 20) {
        displayText = `Sales will begin at ${format(startDate, 'h:mm a')}.`;
      } else if (hoursDiff < 10 * 30 * 24 || startDate.getFullYear() === now.getFullYear()) {
        displayText = `Sales will begin on ${format(startDate, 'MMM do')}.`;
      } else {
        displayText = `Sales will begin in ${startDate.getFullYear()}.`;
      }
      setEmptyListText(displayText);
    } else if (now > endDate) {
      setEmptyListText('This event has ended.');
    }
  }, [event]);

  const loadCoinDeals = useCallback(async () => {
    if (!event || !isGuestSalesActive(event)) return;

    setLoading(true);
    const response = await getMerchandise();
    setLoading(false);

    if (!response.ok) {
      alert("Couldn't retrieve coin sale information. Please try again.");
      return;
    }

    if (response.data?.categories?.[0]) {
      const sorted = [...response.data.categories[0].items].sort(
        (a, b) => b.price - a.price
      );
      setItems(sorted);
      setCategoryId(response.data.categories[0].id);
    }
  }, [event]);

  const loadCards = useCallback(async (password?: string) => {
    if (!loginKey) return;

    setLoading(true);
    const response = await accountCardsWithPassword(loginKey, password);
    setLoading(false);

    if (!response.ok) {
      alert("Couldn't retrieve saved card information.");
      return;
    }

    if (response.data?.require_password && !password) {
      setShowPasswordPrompt(true);
      return;
    }

    if (response.data?.error?.message) {
      setPasswordError(response.data.error.message);
      return;
    }

    if (response.data?.cards) {
      setShowPasswordPrompt(false);
      setPasswordError('');
      const cardOptions = response.data.cards.map((card) => ({
        label: `**** **** **** ${card.last_four}`,
        value: card.access_key,
        last_four: card.last_four,
      }));
      setCards(cardOptions);
    }
  }, [loginKey]);

  useEffect(() => {
    loadCoinDeals();
    loadCards();
  }, [loadCoinDeals, loadCards]);

  const handleQuantityChange = (item: MerchandiseItem, quantity: number) => {
    setSelections((prev) => ({
      ...prev,
      [item.id]: {
        id: item.id,
        unit_price_cents: item.price,
        quantity,
      },
    }));
  };

  const handleBuy = () => {
    if (!itemCount) return;
    setShowPaymentModal(true);
    if (cards.length === 1) {
      setSelectedCard(cards[0].value);
    }
  };

  const handleNewCard = async () => {
    setShowPaymentModal(false);

    if (!loginKey) return;

    setLoading(true);
    const cartResult = await startCart(loginKey);

    if (!cartResult.ok || !cartResult.data?.cart) {
      setLoading(false);
      alert('Unable to open new order.');
      return;
    }

    const cart = cartResult.data.cart;
    const cartContents = Object.values(selections)
      .filter((item) => item.quantity > 0)
      .map((item) => ({
        category_id: categoryId,
        item_id: item.id,
        qty: item.quantity,
        account: account?.id,
      }));

    const contentsResult = await setContents(cart.identifier, cartContents);
    setLoading(false);

    if (!contentsResult.ok || contentsResult.data?.error) {
      alert('Unable to set up order.');
      navigate(-1);
      return;
    }

    const paymentPageUrl = cart.complete_sale_url.replace('customer-info.php', 'pay-online.php');
    const url = saveCard
      ? paymentPageUrl + (paymentPageUrl.includes('?') ? '&' : '?') + 'tkn=y'
      : paymentPageUrl;
    setPaymentUrl(url);
  };

  const handleSavedCard = async () => {
    if (!selectedCard || !loginKey) return;

    const confirmed = window.confirm(
      `Your card will be charged $${(total / 100).toFixed(2)}. Continue?`
    );

    if (!confirmed) {
      setShowPaymentModal(false);
      setSelectedCard('');
      return;
    }

    setShowPaymentModal(false);
    setLoading(true);

    const cartResult = await startCart(loginKey);

    if (!cartResult.ok || !cartResult.data?.cart) {
      setLoading(false);
      alert('Unable to open new order.');
      return;
    }

    const cart = cartResult.data.cart;
    const cartContents = Object.values(selections)
      .filter((item) => item.quantity > 0)
      .map((item) => ({
        category_id: categoryId,
        item_id: item.id,
        qty: item.quantity,
        account: account?.id,
      }));

    const contentsResult = await setContents(cart.identifier, cartContents);

    if (!contentsResult.ok || contentsResult.data?.error) {
      setLoading(false);
      alert('Unable to set up order.');
      navigate(-1);
      return;
    }

    const paymentResult = await cardOnFile(cart.identifier, selectedCard, loginKey);
    setLoading(false);

    if (!paymentResult.ok || !paymentResult.data?.success) {
      alert(`Order Failed: ${paymentResult.data?.message || 'Payment declined'}`);
    } else {
      alert(`Order Complete! ${paymentResult.data.message || ''}`);
    }

    navigate(-1);
  };

  const handleClosePayment = () => {
    setPaymentUrl(null);
    navigate(-1);
  };

  const savedCardText = cards.length === 0
    ? 'You have no saved cards.'
    : cards.length === 1
    ? 'You have 1 saved card.'
    : `You have ${cards.length} saved cards.`;

  return (
    <div
      className={styles.background}
      style={{ backgroundImage: `url(${tocBackground})` }}
    >
      <ActivityIndicator visible={loading} />

      <PasswordDialog
        visible={showPasswordPrompt}
        mode="password_required"
        onSubmit={(passwords) => {
          setPasswordError('');
          loadCards(passwords.currentPassword);
        }}
        onCancel={() => setShowPasswordPrompt(false)}
        error={passwordError}
      />

      {showPaymentModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <button className={styles.closeButton} onClick={() => setShowPaymentModal(false)}>
              ×
            </button>
            <p className={styles.modalTotal}>${(total / 100).toFixed(2)}</p>
            <h3 className={styles.modalTitle}>Select Payment</h3>

            {cards.length >= 2 ? (
              <select
                className={styles.cardSelect}
                value={selectedCard}
                onChange={(e) => setSelectedCard(e.target.value)}
              >
                <option value="">Select a Saved Card</option>
                {cards.map((card) => (
                  <option key={card.value} value={card.value}>
                    {card.label}
                  </option>
                ))}
              </select>
            ) : (
              <p className={styles.cardText}>{savedCardText}</p>
            )}

            {cards.length > 0 && (
              <Button
                theme={selectedCard ? 'secondary' : 'gray'}
                onClick={handleSavedCard}
                disabled={!selectedCard && cards.length > 1}
              >
                {cards.length === 1
                  ? `Use Card Ending In ${cards[0].last_four}`
                  : 'Use Saved Card'}
              </Button>
            )}

            <label className={styles.saveCardLabel}>
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              Save card for future purchases
            </label>

            <Button theme="secondary" onClick={handleNewCard}>
              Use New Card
            </Button>
          </div>
        </div>
      )}

      {paymentUrl && (
        <div className={styles.iframeOverlay}>
          <div className={styles.iframeContainer}>
            <button className={styles.iframeClose} onClick={handleClosePayment}>
              ✕ Close
            </button>
            <iframe src={paymentUrl} className={styles.iframe} />
          </div>
        </div>
      )}

      <div className={styles.container}>
        {sponsorImage && (
          <img src={sponsorImage} alt="Sponsor" className={styles.sponsorImage} />
        )}
        <div className={styles.itemList}>
          {items.length === 0 ? (
            <div className={styles.emptyMessage}>
              <p>{emptyListText}</p>
            </div>
          ) : (
            <>
              {items.map((item) => (
                <RechargeItemPicker
                  key={item.id}
                  item={item}
                  onQuantityChange={handleQuantityChange}
                />
              ))}
              {feeAmount > 0 && (
                <ListItemFee
                  item={{
                    name: event?.fee?.name || 'Convenience Fee',
                    price: feeAmount,
                  }}
                />
              )}
            </>
          )}
        </div>

        <div className={styles.totalBar}>
          <div className={styles.totalInfo}>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total:</span>
              <span className={styles.totalValue}>${(total / 100).toFixed(2)}</span>
            </div>
            <p className={styles.noRefunds}>No Refunds.</p>
          </div>
          <button
            className={`${styles.buyButton} ${itemCount ? styles.buyButtonActive : ''}`}
            onClick={handleBuy}
            disabled={!itemCount}
          >
            BUY
          </button>
        </div>
      </div>
    </div>
  );
}

export default CoinPurchasePage;
